import React, {
  useCallback,
  useState,
} from 'react';
import {
  Props as FieldsArrayWrapperProps,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/FieldsArrayWrapper';
import {
  useFormContext,
  useFieldArray,
} from 'react-hook-form';
import isNil from 'lodash/isNil';
import merge from 'lodash/merge';
import set from 'lodash/set';
import cloneDeep from 'lodash/cloneDeep';
import clsx from 'clsx';
import Button from 'pages/Dashboard/components/Button';
import AddIcon from '@mui/icons-material/Add';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
  DragUpdate,
} from '@hello-pangea/dnd';
import DragNDropIcon from 'core/icons/dragNDrop';

export type Props = FieldsArrayWrapperProps;

const defaultAppendActionProps = {
  text: 'Add',
  variant: 'text',
  color: 'primary',
};

export default function SortableFieldsArrayWrapper({
  title,
  formKey,
  FieldComponent,
  getDefaultValue = () => ({}),
  append: _appendProps,
  className,
  titleClassName,
  keepOne = false,
  patient,
  classes,
}: Props) {
  const {
    control,
    trigger,
    formState,
    watch,
  } = useFormContext();
  const {
    fields,
    append,
    remove,
    update,
    move,
  } = useFieldArray({ control, name: formKey });

  const values = watch(formKey);
  const hasAppend = !isNil(_appendProps);
  const appendProps = hasAppend ? merge({ ...defaultAppendActionProps }, _appendProps) : null;

  const [dragState, setDragState] = useState<DragUpdate | null>(null);

  const onDelete = useCallback((index: number) => {
    if (!keepOne || fields.length > 1) {
      remove(index);
    } else if (keepOne) {
      const defaultRecordValue = getDefaultValue(fields, 'delete');
      update(index, { ...defaultRecordValue });
    }

    // @TODO: need a fix
    // formState has to be immutable
    // this is not a proper way to set field as touched
    set(formState.touchedFields, formKey, true);
  }, [fields, keepOne]);

  const handleAppend = async () => {
    const isValid = await trigger(formKey, { shouldFocus: true });
    if (isValid) {
      const defaultRecordValue = getDefaultValue(fields, 'append');
      append(cloneDeep(defaultRecordValue));
    }
  };

  const onDragEnd = useCallback((result: DropResult) => {
    setDragState(null);
    if (!isNil(result.destination)) {
      move(result.source.index, result.destination.index);
      set(formState.touchedFields, formKey, true);
    }
  }, [move, formState.touchedFields, formKey]);

  const onDragUpdate = useCallback((update: DragUpdate) => {
    setDragState(update);
  }, []);

  const shouldShowLabel = useCallback((index: number, isDragging: boolean) => {
    let showLabel = index === 0 && !isDragging;
    if (!isNil(dragState)) {
      const { source, destination } = dragState;
      let visualFirstIndex = 0;

      if (!isNil(destination)) {
        if (source.index === 0) {
          visualFirstIndex = destination.index === 0 ? source.index : 1;
        } else if (destination.index === 0) {
          visualFirstIndex = source.index;
        }
      } else {
        visualFirstIndex = source.index === 0 ? 1 : 0;
      }

      showLabel = index === visualFirstIndex && !isDragging;
    }

    return showLabel;
  }, [dragState]);

  return (
    <div className={clsx(className, classes?.root)}>
      {!isNil(title) && (
        <h3 className={clsx(
          'text-base font-semibold my-4',
          titleClassName,
          classes?.title,
        )}
        >
          {title}
        </h3>
      )}
      <DragDropContext onDragEnd={onDragEnd} onDragUpdate={onDragUpdate}>
        <Droppable droppableId="droppable">
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={{
                flex: 1,
                gap: '8px',
                paddingBottom: '16px',
                display: 'flex',
                flexDirection: 'column',
                background: snapshot.isDraggingOver ? 'white' : 'white',
              }}
            >
              {fields.map((item, index) => (
                <Draggable key={item.id} draggableId={item.id} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={{
                        ...provided.draggableProps.style,
                        userSelect: 'none',
                        background: snapshot.isDragging ? 'white' : 'white',
                      }}
                    >
                      <div className="flex gap-2 items-center w-full">
                        <DragNDropIcon className={clsx('!text-gray-400 !h-4 !w-4', {
                          '!mt-4': !snapshot.isDragging && index === 0,
                        })}
                        />
                        <div className="flex-1">
                          <FieldComponent
                            key={item.id}
                            showLabel={shouldShowLabel(index, snapshot.isDragging)}
                            index={index}
                            onDelete={onDelete}
                            update={update}
                            prefix={formKey}
                            initialValue={item}
                            value={values[index]}
                            patient={patient}
                            append={handleAppend}
                            isLast={index === (fields.length - 1)}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {hasAppend && (
        <Button
          {...appendProps!}
          className={clsx('!text-xs !font-medium', classes?.add)}
          onClick={handleAppend}
        >
          <AddIcon
            className="mr-1"
            fontSize="small"
          />
          {appendProps!.text}
        </Button>
      )}
    </div>
  );
}
